import { useState } from 'react';
import {
    IComponentCss,
    VerticalTabsCustom,
    TitlePageH1,
} from '@tap/mui-ui';
import { Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TabComponent, TabLabel, TabsTitle } from '../ReportParameterElements';
import { IssueAverageAgeReportResult, docAnalyticsService } from '@tap/data-providers';
import PeriodSelect, { PeriodSelectType, PeriodSelectTypeSpec } from '@tap/mui-ui/src/Form/PeriodSelect';
import AverageAgeReport from './AverageAgeReport';
import { LoadingButton } from '@mui/lab';
import IssueTypeSelect, { IssueDocumentCode } from '@tap/doc-ui/src/components/IssueTypeSelect';
import { SelectDepartmentVerticalTab, useDepartment } from '@tap/tap-ui';
import { NotDataForReportPage } from '../../not-data-for-report/NotDataForReportPage';
import { dateTimeHelper } from '@tap/utils';

/*
*   Макет отчёта
*/

enum CurrentWindow {
    Options = 'options',
    Chart = 'chart',
    NotData = 'not-data',
}

const css: IComponentCss = {
    titleButton: {
        marginTop: '10px',
        minWidth: '189px',
    },
    contentWrap: {
        padding: '0px',
        marginBottom: '34px',
    },
    title: {
        width: '100%',
        marginBottom: '16px',
    },
    dateRangerGrid: {
        marginBottom: '-8px',
    },
};

type AverageAgePageProps = {
    title: string,
}

type AverageAgePageFormProps = {
    periodType: PeriodSelectType,
    departmentIds: number[],
    issueType: IssueDocumentCode,
}

export default function AverageAgePage({ title }: AverageAgePageProps) {
    const [currentWindow, setCurrentWindow] = useState<CurrentWindow>(CurrentWindow.Options);
    const departmentProps = useDepartment();
    const [reportData, setReportData] = useState<IssueAverageAgeReportResult[]>([]);
    const todayStart = new Date(new Date().setHours(0, 0, 0, 0));

    // Запрос расчета
    const { values, setFieldValue, ...formik } = useFormik<AverageAgePageFormProps>({
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            periodType: PeriodSelectType.LastWeek,
            departmentIds: [],
            issueType: IssueDocumentCode.QC,
        },
        validationSchema: Yup.object({
            periodType: Yup.string().required(),
        }),
        onSubmit: async (data) => {
            const isAllDepartmentsSelected = data.departmentIds.length === 0 || data.departmentIds.length === departmentProps.sectorList.length;
            const selectedManufactories = departmentProps.manufactoryList.filter(m => m.sectors.filter(s => data.departmentIds.includes(s.id)).length).map(m => m.name);
            const params = {
                documentCodes: [data.issueType],
                manufactories: selectedManufactories,
                departmentIds: isAllDepartmentsSelected ? [] : data.departmentIds,
                endToUtc: new Date(),
                startFromUtc: new Date(todayStart.setDate(todayStart.getDate() - PeriodSelectTypeSpec[data.periodType].daysQuantity)),
            }
            await docAnalyticsService.getIssueAverageAgeReport(params)
                .then((res) => {
                    if (!res.length) {
                        setCurrentWindow(CurrentWindow.NotData);
                        return;
                    }
                    const data = res?.map(item => ({ ...item, avgTime: dateTimeHelper.getDaysQuantity(item.avgTime)! }))
                    setReportData(data);
                    setCurrentWindow(CurrentWindow.Chart);
                })
        },
    });

    const handleSelectedPeriodChange = (periodType: PeriodSelectType) => {
        setFieldValue('periodType', periodType);
    };

    const handleSelectedTypeChange = (issueType: IssueDocumentCode) => {
        setFieldValue('issueType', issueType);
    };

    return (
        <>

            {currentWindow === CurrentWindow.Options &&
                <>
                    <Grid
                        container
                        mb={4}
                        justifyContent='space-between'
                        sx={{ marginBottom: 0 }}
                    >
                        <TitlePageH1>
                            {title}
                        </TitlePageH1>
                        <Grid>
                            <LoadingButton
                                variant='contained'
                                color='primary'
                                loading={formik.isSubmitting}
                                disabled={
                                    formik.isSubmitting
                                    || !formik.isValid
                                }
                                sx={css.titleButton}
                                onClick={() => {
                                    formik.submitForm();
                                }}
                            >
                                Запрос отчёта
                            </LoadingButton>
                        </Grid>
                    </Grid>
                    <VerticalTabsCustom
                        isCacheTabs
                        defaultTab={1}
                        height={'calc(100vh - 210px)'}
                        tabsArray={[
                            {   // Выберите параметры - заголовок табов
                                tabButton: {
                                    label:
                                        <TabsTitle
                                            title='Параметры'
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        disabled: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: <></>,
                            },
                            {   // Общие
                                tabButton: {
                                    label:
                                        <TabLabel
                                            title={'Общие'}
                                            subTitle={
                                                `Период: ${PeriodSelectTypeSpec[values.periodType]?.name.toLowerCase()}`
                                            }
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent:
                                    <TabComponent title='Общие'>
                                        <>
                                            <Grid
                                                container
                                                sx={{ ...css.contentWrap }}
                                            >
                                                <Typography sx={css.title}>
                                                    Период
                                                </Typography>
                                                <Grid container sx={css.dateRangerGrid}>
                                                    <PeriodSelect
                                                        selectedPeriodType={values.periodType}
                                                        handleSelectedPeriodChange={(periodType) => handleSelectedPeriodChange(periodType)}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                container
                                                sx={{ ...css.contentWrap }}
                                            >
                                                <Typography sx={css.title}>
                                                    Тип заявок
                                                </Typography>
                                                <Grid container sx={css.dateRangerGrid}>
                                                    <IssueTypeSelect
                                                        selectedType={values.issueType}
                                                        handleSelectedTypeChange={(issueType) => handleSelectedTypeChange(issueType)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </>
                                    </TabComponent>,
                            },
                            {   // Цех и участок
                                tabButton: {
                                    label: <SelectDepartmentVerticalTab.TabLabel {...departmentProps}
                                        selectedDepartmentIds={values.departmentIds}
                                    />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: (
                                    <SelectDepartmentVerticalTab.TabContent {...departmentProps}
                                        selectedDepartmentIds={values.departmentIds}
                                        onChange={(departmentIds) => setFieldValue('departmentIds', departmentIds)}
                                    />
                                ),
                            },
                        ]
                        }
                    />
                </>
            }

            {/* График */}
            {currentWindow === CurrentWindow.Chart &&
                <AverageAgeReport
                    periodType={values.periodType}
                    reportData={reportData}
                    onOptionsButtonClick={() => setCurrentWindow(CurrentWindow.Options)} />
            }

            {/* Заглушка на случай, если нет данных для формирования отчета. */}
            {currentWindow === CurrentWindow.NotData &&
                <NotDataForReportPage onReturnButtonClick={() => setCurrentWindow(CurrentWindow.Options)} />
            }
        </>
    );
}
